import { MediaController } from 'media-chrome';
import { MediaUIEvents } from 'media-chrome/dist/constants.js';
import { isIOS } from '../utils/browser-detection.js';

const NUMERIC_KEYS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
const ATTR_PLAYBACK_RATES = 'playbackrates' as const;

/**
 * Custom media controller component that extends Media Chrome's controller with additional keyboard shortcuts
 *
 * Adds support for numeric key shortcuts (0-9) to seek to specific video positions:
 * - 0: Seek to 0% of video duration
 * - 1: Seek to 10% of video duration
 * - 2: Seek to 20% of video duration
 * ...
 * - 9: Seek to 90% of video duration
 *
 * @attr {string} playbackrates - Space-separated list of playback rates (e.g., "0.5 0.75 1 1.25 2")
 */
export class BunnyMediaController extends MediaController {
  // iOS native fullscreen handlers: when playsinline=false, iOS uses its own
  // fullscreen player. On dismissal (swipe down), iOS shows the video inline
  // with native controls. We hide custom controls to prevent overlapping UI.
  #iosFullscreenBeginHandler = (e: Event) => {
    if (e.target !== this.media) return;
    this.removeAttribute('data-ios-native-ui');
  };

  #iosFullscreenEndHandler = (e: Event) => {
    const mediaEl = e.target as HTMLElement | null;
    if (!mediaEl || mediaEl !== this.media) return;

    if (
      !mediaEl.hasAttribute('playsinline') &&
      !mediaEl.hasAttribute('webkit-playsinline')
    ) {
      this.setAttribute('data-ios-native-ui', '');
    }
  };

  #numericKeyHandler = (e: KeyboardEvent) => {
    const { metaKey, altKey, ctrlKey, shiftKey, key, target } = e;

    // Only handle numeric keys without modifiers
    if (
      metaKey || altKey || ctrlKey || shiftKey || !NUMERIC_KEYS.includes(key)
    ) {
      return;
    }

    // Ignore if typing in editable fields
    const el = target as HTMLElement | null;
    const isEditable = !!el && (
      el.tagName === 'INPUT' ||
      el.tagName === 'TEXTAREA' ||
      (el as HTMLElement).isContentEditable
    );
    if (isEditable) return;

    const percentage = parseInt(key, 10) * 10; // Convert key to percentage (0-90)
    const duration = this.mediaStore.getState().mediaDuration;

    if (duration) {
      const targetTime = (duration * percentage) / 100;
      e.preventDefault();
      this.mediaStore?.dispatch({
        type: MediaUIEvents.MEDIA_SEEK_REQUEST,
        detail: targetTime,
      });
    }
  };

  constructor() {
    super();
    // Add our numeric key handler
    this.addEventListener('keydown', this.#numericKeyHandler);

    // Listen for media control events to ensure we get focus back
    this.addEventListener(MediaUIEvents.MEDIA_PLAY_REQUEST, () => {
      requestAnimationFrame(() => this.focus());
    });
    this.addEventListener(MediaUIEvents.MEDIA_PAUSE_REQUEST, () => {
      requestAnimationFrame(() => this.focus());
    });
  }

  static override get observedAttributes(): string[] {
    return [...super.observedAttributes, ATTR_PLAYBACK_RATES];
  }

  override connectedCallback(): void {
    super.connectedCallback();
    // Ensure the controller is focusable
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback?.();
    // Clean up our event listener
    this.removeEventListener('keydown', this.#numericKeyHandler);
  }

  override attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (oldValue === newValue) return;

    if (name === ATTR_PLAYBACK_RATES && this.media) {
      this.#initializePlaybackRate(this.media);
    }
  }

  override mediaSetCallback(media: HTMLMediaElement): void {
    super.mediaSetCallback(media);
    // Initialize playback rate if attribute is already set
    if (this.hasAttribute(ATTR_PLAYBACK_RATES)) {
      this.#initializePlaybackRate(media);
    }
    // On iOS, track native fullscreen state to hide custom controls
    // when the video exits native fullscreen without playsinline
    if (isIOS()) {
      media.addEventListener(
        'webkitbeginfullscreen',
        this.#iosFullscreenBeginHandler,
      );
      media.addEventListener(
        'webkitendfullscreen',
        this.#iosFullscreenEndHandler,
      );
    }
  }

  override mediaUnsetCallback(media: HTMLMediaElement): void {
    super.mediaUnsetCallback(media);
    media.removeEventListener(
      'webkitbeginfullscreen',
      this.#iosFullscreenBeginHandler,
    );
    media.removeEventListener(
      'webkitendfullscreen',
      this.#iosFullscreenEndHandler,
    );
    this.removeAttribute('data-ios-native-ui');
  }

  #initializePlaybackRate(media: HTMLMediaElement): void {
    const playbackRates = this.getAttribute(ATTR_PLAYBACK_RATES);
    if (!playbackRates) return;

    // Parse rates (space-separated string)
    const rates = playbackRates
      .split(/\s+/)
      .map((r) => parseFloat(r))
      .filter((r) => !isNaN(r));

    if (rates.length === 0) return;

    // Find 1.0 or closest value to 1.0
    const targetRate = rates.find((r) => r === 1.0) ||
      rates.reduce(
        (closest, current) =>
          Math.abs(current - 1.0) < Math.abs(closest - 1.0) ? current : closest,
        rates[0],
      );

    // Set initial playback rate when media is ready
    const setInitialRate = () => {
      if (media.readyState >= 1) {
        this.mediaStore?.dispatch({
          type: MediaUIEvents.MEDIA_PLAYBACK_RATE_REQUEST,
          detail: targetRate,
        });
        media.removeEventListener('loadedmetadata', setInitialRate);
        media.removeEventListener('loadeddata', setInitialRate);
      }
    };

    if (media.readyState >= 1) {
      setInitialRate();
    } else {
      media.addEventListener('loadedmetadata', setInitialRate, { once: true });
      media.addEventListener('loadeddata', setInitialRate, { once: true });
    }
  }

  // Public API
  get playbackRates(): string | null {
    return this.getAttribute(ATTR_PLAYBACK_RATES);
  }

  set playbackRates(value: string | null) {
    if (value) {
      this.setAttribute(ATTR_PLAYBACK_RATES, value);
    } else {
      this.removeAttribute(ATTR_PLAYBACK_RATES);
    }
  }
}

// Register the custom element if it hasn't been registered yet
if (!globalThis.customElements.get('media-controller')) {
  globalThis.customElements.define('media-controller', BunnyMediaController);
}

export default BunnyMediaController;
